import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { toISO } from '../utils/dates';
import { formatAllergens } from '../utils/allergens';

type ResultState = {
  type: 'success' | 'warning' | 'error';
  title: string;
  dishName?: string;
  allergens?: string[];
  orderId?: string;
} | null;

const RESET_SECONDS = 3;
const CODE_PATTERN = /^\d[A-Z]\d[A-Z]$/;
const KEYBOARD_ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
];

export function PickupPage() {
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState<ResultState>(null);
  const [redeemed, setRedeemed] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const todayISO = toISO(new Date());

  const reset = () => {
    setCode('');
    setResult(null);
    setRedeemed(false);
    setCountdown(null);
  };

  // Countdown nach eingelöstem Code
  useEffect(() => {
    if (!redeemed) return;
    setCountdown(RESET_SECONDS);
  }, [redeemed]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) { reset(); return; }
    const t = setTimeout(() => setCountdown((c) => (c !== null ? c - 1 : null)), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const isCodeValid = CODE_PATTERN.test(code);

  const handleCheck = async () => {
    if (!isCodeValid) return;
    setChecking(true);
    setResult(null);
    setRedeemed(false);

    try {
      const res = await api.pickup.validate(code.toUpperCase(), todayISO);

      if (!res.found) {
        setResult({
          type: 'warning',
          title: res.message ?? (res.codeAssigned === false
            ? 'Dieser Code ist keinem Nutzer zugeordnet.'
            : 'Kein Gericht heute mit diesem Code gefunden.'),
        });
      } else if (res.alreadyRedeemed) {
        setResult({
          type: 'error',
          title: 'Dieser Code wurde heute bereits verwendet.',
          dishName: res.dish?.name,
          allergens: res.dish?.allergens,
        });
      } else {
        setResult({
          type: 'success',
          title: 'Gericht gefunden',
          dishName: res.dish?.name,
          allergens: res.dish?.allergens,
          orderId: res.orderId,
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler';
      setResult({ type: 'error', title: `Fehler: ${message}` });
    } finally {
      setChecking(false);
    }
  };

  const handleRedeem = async () => {
    if (!result?.orderId) return;
    setRedeeming(true);
    try {
      await api.pickup.redeem(code.toUpperCase(), todayISO);
      setRedeemed(true);
      setResult((prev) => prev ? { ...prev, title: 'Code eingelöst – Mahlzeit!' } : prev);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler';
      setResult({ type: 'error', title: `Fehler: ${message}` });
    } finally {
      setRedeeming(false);
    }
  };

  const appendChar = (char: string) => {
    if (code.length >= 4) return;
    setCode((prev) => prev + char);
    setResult(null);
    setRedeemed(false);
  };

  const handleBackspace = () => {
    setCode((prev) => prev.slice(0, -1));
    setResult(null);
    setRedeemed(false);
  };

  return (
    <div className="pickup-page">
      <div className="pickup-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="pickup-header-logo">Foody</span>
          <span style={{ color: 'var(--border)' }}>·</span>
          <span className="pickup-header-sub">Essensausgabe</span>
        </div>
      </div>

      <div className="pickup-content">
        <div className="pickup-card">
          <div>
            <div className="pickup-title">Abholcode prüfen</div>
            <div className="pickup-subtitle">
              Heute: {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' })}
            </div>
          </div>

          <div className={`pickup-result ${result ? result.type : 'empty'}`}>
            {result && (
              <>
                <div className="pickup-result-title">{result.title}</div>
                {result.dishName && (
                  <div className="pickup-result-dish">{result.dishName}</div>
                )}
                {result.allergens && result.allergens.length > 0 && (
                  <div className="pickup-result-allergens">
                    Allergene: {formatAllergens(result.allergens)}
                  </div>
                )}
                {result.type === 'success' && !redeemed && result.orderId && (
                  <button
                    className="btn btn-primary"
                    style={{ marginTop: '0.875rem', width: '100%' }}
                    onClick={handleRedeem}
                    disabled={redeeming}
                  >
                    {redeeming ? 'Wird eingelöst…' : '✓ Ausgabe bestätigen'}
                  </button>
                )}
                {redeemed && (
                  <div style={{ marginTop: '0.625rem', textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, color: 'var(--success-text)', fontSize: '1.125rem', marginBottom: '0.875rem' }}>
                      ✓ Guten Appetit!
                    </div>
                    <div style={{ fontSize: '0.813rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                      Nächste Person in {countdown}…
                    </div>
                    <button className="btn btn-secondary" style={{ width: '100%' }} onClick={reset}>
                      Nächste Person →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="pickup-input-group">
            <div className={`pickup-code-display${code.length === 4 && !isCodeValid ? ' invalid' : ''}`}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={`pickup-code-slot${code[i] ? ' filled' : ''}`}>
                  {code[i] ?? ''}
                </div>
              ))}
            </div>

            <div className={`pickup-code-hint${code.length === 4 && !isCodeValid ? '' : ' empty'}`}>
              Ungültiges Format (z.B. 6F3B: Ziffer-Buchstabe-Ziffer-Buchstabe)
            </div>

            <div className="pickup-keyboard">
              {KEYBOARD_ROWS.map((row, i) => (
                <div key={i} className="pickup-keyboard-row">
                  {row.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="pickup-key"
                      onClick={() => appendChar(key)}
                      disabled={code.length >= 4}
                    >
                      {key}
                    </button>
                  ))}
                  {i === 0 && (
                    <button
                      type="button"
                      className="pickup-key pickup-key-backspace"
                      onClick={handleBackspace}
                      disabled={!code}
                      aria-label="Löschen"
                    >
                      ⌫
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="pickup-actions">
              <button
                className="btn btn-primary btn-lg"
                style={{ flex: 1 }}
                onClick={handleCheck}
                disabled={checking || !isCodeValid}
              >
                {checking ? 'Wird geprüft…' : 'Prüfen'}
              </button>
              <button
                className="btn btn-secondary btn-lg"
                onClick={reset}
                disabled={!code && !result}
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
